import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Image as ImageIcon, Loader2, Palette, Settings } from 'lucide-react';
import { IconBrandAbstract, IconFileTextShield, IconPencilCog } from '@tabler/icons-react';
import '@fontsource-variable/geist/wght.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import '../../process-config/ConfigStatusView2.css';
import '../../process-config/components/DefeitosVerveTableV2.css';
import {
  ColorSwatchCellDv2,
  DefeitosColorPalettePopover,
  isDefeitoColorConfigured,
} from '../../process-config/components/defeitosVerveTableUi';
import {
  compressGoBrandLogo,
  GO_BRAND_LOGO_ACCEPT,
  GO_BRAND_LOGO_MAX_BYTES,
} from '../../go-config/utils/compressGoBrandLogo';
import {
  FAVICON_ACCEPT,
  validatePortalFaviconFile,
} from '../utils/validatePortalFavicon';
import '../../go-config/components/GoCustomizacaoConfig.css';
import CustomersEmpresasModal from './CustomersEmpresasModal';
import CustomersPoliticasModal from './CustomersPoliticasModal';
import './CustomersCustomizacaoConfig.css';

const API_BASE_URL = getApiBaseUrl();
const DEFAULT_PRIMARY_COLOR = '#0066CC';
const EMPRESA_NOME_MAX = 20;
const PAGE_TITLE_MAX = 100;
const EMPRESA_API = `${API_BASE_URL}/api/config/vc-sys-config/empresa-principal`;
const FAVICON_HINT = 'Somente .ico, 16×16 (recomendado) ou 32×32';

function Pcs2Section({ icon: Icon, title, desc, children, className = '' }) {
  return (
    <section className={['pcs2-section', className].filter(Boolean).join(' ')}>
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        {desc ? <p className="pcs2-section-desc">{desc}</p> : null}
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function Pcs2Subblock({ title, desc, children }) {
  return (
    <div className="pcs2-subblock">
      {(title || desc) ? (
        <div className="pcs2-subhead">
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

function Pcs2TooltipHost({ text, children, align = 'start' }) {
  if (!text) return children;
  return (
    <span
      className={[
        'pcs2-tooltip-host',
        align === 'end' ? 'pcs2-tooltip-host--align-end' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-tooltip={text}
    >
      {children}
    </span>
  );
}

function Pcs2SettingRow({ title, desc, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">{title}</span>
        {desc ? <span className="pcs2-setting-desc">{desc}</span> : null}
      </div>
      <div className="pcs2-setting-actions">{children}</div>
    </div>
  );
}

function normalizeHex(value) {
  const c = String(value ?? '').trim();
  if (!c) return '';
  const withHash = c.startsWith('#') ? c : `#${c}`;
  return withHash.slice(0, 7).toUpperCase();
}

export default function CustomersCustomizacaoConfig() {
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();
  const colorBtnRef = useRef(null);
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const previewBlobRef = useRef(null);
  const faviconPreviewBlobRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [logoCompressing, setLogoCompressing] = useState(false);
  const [empresasOpen, setEmpresasOpen] = useState(false);
  const [politicasOpen, setPoliticasOpen] = useState(false);

  const [savedPrimaryColor, setSavedPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [savedEmpresaNome, setSavedEmpresaNome] = useState('');
  const [empresaNome, setEmpresaNome] = useState('');
  const [empresaLogoUrl, setEmpresaLogoUrl] = useState(null);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const [savedPageTitle, setSavedPageTitle] = useState('');
  const [pageTitle, setPageTitle] = useState('');
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [pendingFaviconFile, setPendingFaviconFile] = useState(null);

  const revokePreview = useCallback(() => {
    if (previewBlobRef.current) {
      URL.revokeObjectURL(previewBlobRef.current);
      previewBlobRef.current = null;
    }
  }, []);

  const revokeFaviconPreview = useCallback(() => {
    if (faviconPreviewBlobRef.current) {
      URL.revokeObjectURL(faviconPreviewBlobRef.current);
      faviconPreviewBlobRef.current = null;
    }
  }, []);

  const isDirty = useMemo(() => {
    if (loading || error) return false;
    const colorChanged = normalizeHex(primaryColor) !== normalizeHex(savedPrimaryColor);
    const nomeChanged =
      String(empresaNome || '').trim() !== String(savedEmpresaNome || '').trim();
    const logoChanged = pendingLogoFile != null;
    const titleChanged =
      String(pageTitle || '').trim() !== String(savedPageTitle || '').trim();
    const faviconChanged = pendingFaviconFile != null;
    return colorChanged || nomeChanged || logoChanged || titleChanged || faviconChanged;
  }, [
    loading,
    error,
    primaryColor,
    savedPrimaryColor,
    empresaNome,
    savedEmpresaNome,
    pendingLogoFile,
    pageTitle,
    savedPageTitle,
    pendingFaviconFile,
  ]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [colorRes, empresaRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/config/vc-sys-config/color-primary`, { headers }),
        fetch(EMPRESA_API, { headers }),
      ]);
      const colorData = await colorRes.json();
      if (!colorRes.ok || !colorData.success) {
        throw new Error(colorData.message || 'Falha ao carregar customização');
      }
      const color = normalizeHex(colorData.data?.primaryColor) || DEFAULT_PRIMARY_COLOR;
      setPrimaryColor(color);
      setSavedPrimaryColor(color);

      const empresaData = await empresaRes.json();
      if (!empresaRes.ok || !empresaData.success) {
        throw new Error(empresaData.message || 'Falha ao carregar empresa principal');
      }
      revokePreview();
      revokeFaviconPreview();
      setPendingLogoFile(null);
      setPendingFaviconFile(null);
      const nome = String(empresaData.data?.nome || '').slice(0, EMPRESA_NOME_MAX);
      setEmpresaNome(nome);
      setSavedEmpresaNome(nome);
      setEmpresaLogoUrl(empresaData.data?.viewUrl || null);
      const title = String(empresaData.data?.pageTitle || '').slice(0, PAGE_TITLE_MAX);
      setPageTitle(title);
      setSavedPageTitle(title);
      setFaviconUrl(empresaData.data?.faviconUrl || null);
    } catch (e) {
      setError(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [revokePreview, revokeFaviconPreview]);

  useEffect(() => {
    loadData();
    return () => {
      revokePreview();
      revokeFaviconPreview();
    };
  }, [loadData, revokePreview, revokeFaviconPreview]);

  const onColorDraftChange = useCallback((hex) => {
    const color = normalizeHex(hex);
    if (!isDefeitoColorConfigured(color)) return;
    setPrimaryColor(color);
  }, []);

  const onEmpresaNomeChange = useCallback((event) => {
    setEmpresaNome(String(event.target.value || '').slice(0, EMPRESA_NOME_MAX));
  }, []);

  const onPageTitleChange = useCallback((event) => {
    setPageTitle(String(event.target.value || '').slice(0, PAGE_TITLE_MAX));
  }, []);

  const handleLogoSelect = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      if (!String(file.type || '').startsWith('image/')) {
        toastError('Selecione somente arquivos de imagem.');
        return;
      }
      if (file.size > GO_BRAND_LOGO_MAX_BYTES) {
        toastError('Imagem muito grande. Máximo 20MB.');
        return;
      }

      setLogoCompressing(true);
      try {
        const compressed = await compressGoBrandLogo(file);
        revokePreview();
        const localPreview = URL.createObjectURL(compressed);
        previewBlobRef.current = localPreview;
        setPendingLogoFile(compressed);
        setEmpresaLogoUrl(localPreview);
      } catch (e) {
        toastError(e.message || 'Erro ao processar logo');
      } finally {
        setLogoCompressing(false);
      }
    },
    [revokePreview, toastError]
  );

  const handleFaviconSelect = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      let buffer;
      try {
        buffer = await file.arrayBuffer();
      } catch {
        toastError('Não foi possível ler o arquivo. Envie um favicon .ico válido.');
        return;
      }

      const checked = validatePortalFaviconFile(file, buffer);
      if (!checked.ok) {
        toastError(checked.message);
        return;
      }

      revokeFaviconPreview();
      const localPreview = URL.createObjectURL(file);
      faviconPreviewBlobRef.current = localPreview;
      setPendingFaviconFile(file);
      setFaviconUrl(localPreview);
    },
    [revokeFaviconPreview, toastError]
  );

  const handleSave = useCallback(async () => {
    if (!isDirty || isSaving) return;
    setIsSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const color = normalizeHex(primaryColor);
      if (color !== normalizeHex(savedPrimaryColor)) {
        if (!isDefeitoColorConfigured(color)) {
          throw new Error('Informe uma cor válida');
        }
        const res = await fetch(`${API_BASE_URL}/api/config/vc-sys-config/color-primary`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ color_primary: color }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Falha ao salvar cor');
        }
        const savedColor = normalizeHex(data.data?.primaryColor) || color;
        setPrimaryColor(savedColor);
        setSavedPrimaryColor(savedColor);
      }

      const nome = String(empresaNome || '').trim().slice(0, EMPRESA_NOME_MAX);
      if (nome !== String(savedEmpresaNome || '').trim()) {
        const res = await fetch(`${EMPRESA_API}/nome`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ nome }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Falha ao salvar nome');
        }
        const savedNome = String(data.data?.nome ?? nome).slice(0, EMPRESA_NOME_MAX);
        setEmpresaNome(savedNome);
        setSavedEmpresaNome(savedNome);
      }

      if (pendingLogoFile) {
        const compressed = pendingLogoFile;
        const presignRes = await fetch(`${EMPRESA_API}/logo/presign`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            mimeType: compressed.type,
            sizeBytes: compressed.size,
            fileName: compressed.name,
          }),
        });
        const presignData = await presignRes.json();
        if (!presignRes.ok || !presignData.success) {
          throw new Error(presignData.message || 'Falha ao preparar upload');
        }
        const uploadUrl = presignData.data?.uploadUrl;
        const key = presignData.data?.key;
        if (!uploadUrl || !key) {
          throw new Error('Resposta de upload inválida');
        }

        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': compressed.type },
          body: compressed,
        });
        if (!uploadRes.ok) {
          throw new Error('Falha ao enviar imagem para o armazenamento');
        }

        const confirmRes = await fetch(`${EMPRESA_API}/logo/complete`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ s3Key: key }),
        });
        const confirmData = await confirmRes.json();
        if (!confirmRes.ok || !confirmData.success) {
          throw new Error(confirmData.message || 'Falha ao salvar logo');
        }

        const logoUrl = confirmData.data?.logoUrl || empresaLogoUrl;
        setPendingLogoFile(null);
        setEmpresaLogoUrl(logoUrl);
      }

      const title = String(pageTitle || '').trim().slice(0, PAGE_TITLE_MAX);
      if (title !== String(savedPageTitle || '').trim()) {
        const res = await fetch(`${EMPRESA_API}/page-title`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ pageTitle: title }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Falha ao salvar título da página');
        }
        const savedTitle = String(data.data?.pageTitle ?? title).slice(0, PAGE_TITLE_MAX);
        setPageTitle(savedTitle);
        setSavedPageTitle(savedTitle);
      }

      if (pendingFaviconFile) {
        const file = pendingFaviconFile;
        const presignRes = await fetch(`${EMPRESA_API}/favicon/presign`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            mimeType: file.type || 'image/x-icon',
            sizeBytes: file.size,
            fileName: file.name || 'favicon.ico',
          }),
        });
        const presignData = await presignRes.json();
        if (!presignRes.ok || !presignData.success) {
          throw new Error(presignData.message || 'Falha ao preparar upload do favicon');
        }
        const uploadUrl = presignData.data?.uploadUrl;
        const key = presignData.data?.key;
        const mime = presignData.data?.mime || file.type || 'image/x-icon';
        if (!uploadUrl || !key) {
          throw new Error('Resposta de upload inválida');
        }

        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': mime },
          body: file,
        });
        if (!uploadRes.ok) {
          throw new Error('Falha ao enviar favicon para o armazenamento');
        }

        const confirmRes = await fetch(`${EMPRESA_API}/favicon/complete`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ s3Key: key }),
        });
        const confirmData = await confirmRes.json();
        if (!confirmRes.ok || !confirmData.success) {
          throw new Error(confirmData.message || 'Falha ao salvar favicon');
        }

        revokeFaviconPreview();
        setPendingFaviconFile(null);
        setFaviconUrl(confirmData.data?.faviconUrl || faviconUrl);
      }

      toastSuccess('Configuração salva');
    } catch (e) {
      toastError(e.message || 'Erro ao salvar configuração');
    } finally {
      setIsSaving(false);
    }
  }, [
    isDirty,
    isSaving,
    primaryColor,
    savedPrimaryColor,
    empresaNome,
    savedEmpresaNome,
    pendingLogoFile,
    empresaLogoUrl,
    pageTitle,
    savedPageTitle,
    pendingFaviconFile,
    faviconUrl,
    revokeFaviconPreview,
    toastError,
    toastSuccess,
  ]);

  const busy = isSaving || logoCompressing;

  return (
    <div className="pcs2-page go-customizacao-page customers-customizacao-page">
      <Pcs2Section
        className="go-customizacao-section"
        icon={Palette}
        title="Customização"
        desc="Configure a cor predominante da marca no portal Customers"
      >
        <Pcs2Subblock title="Cor predominante" desc="Cor predominante padrão no portal Customers">
          {loading ? (
            <ProcessConfigSkeleton variant="settings" embedded ariaLabel="Carregando customização" />
          ) : error ? (
            <div className="go-cust-error">
              <span>{error}</span>
              <button type="button" className="go-cust-retry" onClick={loadData}>
                Tentar novamente
              </button>
            </div>
          ) : (
            <>
              <div className="go-cust-color-row">
                <button
                  ref={colorBtnRef}
                  type="button"
                  className="go-cust-color-trigger"
                  disabled={busy}
                  aria-label="Alterar cor do App"
                  onClick={() => setColorOpen((open) => !open)}
                >
                  <ColorSwatchCellDv2 hex={primaryColor} />
                  <span className="go-cust-color-hex">{primaryColor}</span>
                </button>
              </div>
              <DefeitosColorPalettePopover
                open={colorOpen}
                anchorRef={colorBtnRef}
                draftColor={primaryColor}
                onDraftChange={onColorDraftChange}
                onClose={() => setColorOpen(false)}
              />
            </>
          )}
        </Pcs2Subblock>

        <div className="pcs2-card pcs2-card--flat">
          <Pcs2SettingRow
            title="Nome e logo da empresa principal"
            desc="Definir aqui a imagem ou logo, e também nome que será exibido na tela principal"
          >
            {loading || error ? null : (
              <>
                <Pcs2TooltipHost text="Atualizar logo da empresa principal" align="end">
                  <button
                    type="button"
                    className="customers-principal-logo-btn"
                    disabled={busy}
                    aria-label="Atualizar logo da empresa principal"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {empresaLogoUrl ? (
                      <img src={empresaLogoUrl} alt="" className="customers-principal-logo-img" />
                    ) : (
                      <ImageIcon size={16} strokeWidth={1.75} aria-hidden />
                    )}
                  </button>
                </Pcs2TooltipHost>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept={GO_BRAND_LOGO_ACCEPT}
                  className="go-cust-logo-input-hidden"
                  onChange={handleLogoSelect}
                />
                <Pcs2TooltipHost text="Nome exibido na tela principal (máx. 20)" align="end">
                  <label className="go-cust-color-trigger customers-principal-name-trigger">
                    <input
                      type="text"
                      className="customers-principal-name-input"
                      value={empresaNome}
                      maxLength={EMPRESA_NOME_MAX}
                      disabled={busy}
                      placeholder="Nome"
                      aria-label="Nome da empresa principal"
                      onChange={onEmpresaNomeChange}
                    />
                    <span className="customers-principal-name-count" aria-hidden>
                      {empresaNome.length}/{EMPRESA_NOME_MAX}
                    </span>
                  </label>
                </Pcs2TooltipHost>
              </>
            )}
          </Pcs2SettingRow>
          <Pcs2SettingRow
            title="Título da página"
            desc="Texto exibido na aba do navegador (máximo 100 caracteres)"
          >
            {loading || error ? null : (
              <Pcs2TooltipHost text="Título da página no navegador (máx. 100)" align="end">
                <label className="go-cust-color-trigger customers-principal-name-trigger customers-page-title-trigger">
                  <input
                    type="text"
                    className="customers-principal-name-input"
                    value={pageTitle}
                    maxLength={PAGE_TITLE_MAX}
                    disabled={busy}
                    placeholder="Título"
                    aria-label="Título da página"
                    onChange={onPageTitleChange}
                  />
                  <span className="customers-principal-name-count" aria-hidden>
                    {pageTitle.length}/{PAGE_TITLE_MAX}
                  </span>
                </label>
              </Pcs2TooltipHost>
            )}
          </Pcs2SettingRow>
          <Pcs2SettingRow
            title="Favicon da página"
            desc="Ícone da aba do navegador. Somente arquivo .ico, 16×16 (recomendado) ou 32×32. Outros formatos e tamanhos não são aceitos."
          >
            {loading || error ? null : (
              <>
                <Pcs2TooltipHost text={FAVICON_HINT} align="end">
                  <button
                    type="button"
                    className="customers-principal-logo-btn customers-principal-favicon-btn"
                    disabled={busy}
                    aria-label="Atualizar favicon da página"
                    onClick={() => faviconInputRef.current?.click()}
                  >
                    {faviconUrl ? (
                      <img src={faviconUrl} alt="" className="customers-principal-logo-img" />
                    ) : (
                      <ImageIcon size={14} strokeWidth={1.75} aria-hidden />
                    )}
                  </button>
                </Pcs2TooltipHost>
                <input
                  ref={faviconInputRef}
                  type="file"
                  accept={FAVICON_ACCEPT}
                  className="go-cust-logo-input-hidden"
                  onChange={handleFaviconSelect}
                />
                <span className="customers-favicon-spec" aria-hidden>
                  .ico · 16×16 ou 32×32
                </span>
              </>
            )}
          </Pcs2SettingRow>
        </div>
      </Pcs2Section>

      <hr className="pcs2-divider" />

      <Pcs2Section
        className="go-customizacao-section"
        icon={IconBrandAbstract}
        title="Empresas"
        desc="Configure nome de exibição de cada empresa e logo"
      >
        <div className="pcs2-card pcs2-card--flat">
          <Pcs2SettingRow
            title="Nome e logo das empresas"
            desc="Configure o nome de exibição e a logomarca de cada empresa no portal Customers"
          >
            <Pcs2TooltipHost text="Configurar nome e logo por empresa" align="end">
              <button
                type="button"
                className="pcs2-btn-gear"
                onClick={() => setEmpresasOpen(true)}
                aria-label="Configurar nome e logo das empresas"
              >
                <Settings size={15} strokeWidth={1.75} />
              </button>
            </Pcs2TooltipHost>
          </Pcs2SettingRow>
        </div>
      </Pcs2Section>

      <hr className="pcs2-divider" />

      <Pcs2Section
        className="go-customizacao-section"
        icon={IconFileTextShield}
        title="Políticas de segurança"
        desc="Defina aqui termo de uso e política de segurança do portal"
      >
        <div className="pcs2-card pcs2-card--flat">
          <Pcs2SettingRow
            title="Termo de uso e política de privacidade"
            desc="Edite os textos legais exibidos no portal Customers"
          >
            <Pcs2TooltipHost text="Configurar políticas de segurança" align="end">
              <button
                type="button"
                className="pcs2-btn-gear"
                onClick={() => setPoliticasOpen(true)}
                aria-label="Configurar políticas de segurança"
              >
                <IconPencilCog size={15} strokeWidth={1.75} />
              </button>
            </Pcs2TooltipHost>
          </Pcs2SettingRow>
        </div>
      </Pcs2Section>

      <CustomersEmpresasModal
        open={empresasOpen}
        onClose={() => setEmpresasOpen(false)}
        onToastSuccess={toastSuccess}
        onToastError={toastError}
      />
      <CustomersPoliticasModal
        open={politicasOpen}
        onClose={() => setPoliticasOpen(false)}
        onToastSuccess={toastSuccess}
        onToastError={toastError}
      />
      {Toaster}

      {typeof document !== 'undefined'
        ? createPortal(
            <button
              type="button"
              className="pcs2-save-fab"
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              aria-label="Salvar configuração"
            >
              {isSaving ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : null}
              {isSaving ? 'Salvando...' : 'Salvar configuração'}
            </button>,
            document.body
          )
        : null}
    </div>
  );
}
